/* -----------------Globals--------------- */
import type { UnifiedCitation } from "@memorang/types/src/session/new-session";
/* -----------------Child components--------------- */
import Box from "../Box";
import ChatStartViewGroup from "../ChatStartViewGroup";
import { CustomListSubheader } from "../CustomListSubheader";
import { FadeView } from "../FadeView";
import { ChatMessage } from "./ChatMessage";
import { RecommendedChipsSkeleton } from "./RecommendedChipsSkeleton";
/* -----------------Types--------------- */
import type { AiAssistantInfo, GetMessageActions, MessageData } from "./types";

type Props = {
	assistantInfo: AiAssistantInfo;
	messageActions?: GetMessageActions;
	streaming?: boolean;
	messages: MessageData[];
	recommendedQueries: string[];
	handleSelectQuery: (query: string) => void;
	showRecommendedChipsLoading?: boolean;
	loader?: React.ReactNode;
	handleNavigateToReference?: (url: string) => void;
	showReference?: boolean;
	showSummarizeResponse?: boolean;
	referenceThumbnails?: Record<string, string>;
	showError?: boolean;
	errorMessage?: string;
	clearError?: () => void;
	textToSpeach: (runId: string, content: string) => React.ReactNode;
	handleShowAllSources: (sources: UnifiedCitation[]) => void;
};

export const ChatList = ({
	assistantInfo,
	messageActions,
	messages,
	recommendedQueries = [],
	handleSelectQuery,
	showRecommendedChipsLoading = false,
	loader,
	handleNavigateToReference,
	showReference,
	showSummarizeResponse,
	referenceThumbnails,
	handleShowAllSources,
	textToSpeach,
}: Props) => {
	return (
		<>
			{messages.length > 1 ? (
				<>
					{messages.map(
						(
							{
								type,
								loading,
								content,
								streaming,
								references,
								id,
								runId,
								tools: messageToolInfo,
								errorMessage,
								onPressRetry,
							},
							index,
						) => {
							const shouldShowFollowUpHeader =
								type === "assistant" &&
								index === messages.length - 1 &&
								recommendedQueries.length > 0 &&
								!errorMessage &&
								!loading &&
								!streaming;
							const shouldShowRecommendedChips =
								type === "assistant" &&
								index === messages.length - 1 &&
								!messages[index].streaming &&
								!messages[index].loading &&
								recommendedQueries.length > 0;
							return (
								<FadeView key={`${id}`} duration={500}>
									<Box gap={16}>
										<Box
											alignItems={type === "human" ? "flex-end" : "flex-start"}
											flexDirection="column"
											maxWidth="100%"
											gap={16}
											paddingHorizontal={16}
										>
											<ChatMessage
												type={type}
												loading={loading}
												content={content}
												streaming={streaming}
												references={references}
												loader={loader}
												handleNavigateToReference={handleNavigateToReference}
												showReference={showReference}
												messageActions={messageActions}
												referenceThumbnails={referenceThumbnails}
												assistantInfo={assistantInfo}
												runId={runId}
												toolInfo={messageToolInfo}
												textToSpeach={textToSpeach}
												handleShowAllSources={handleShowAllSources}
												errorMessage={errorMessage}
												onPressRetry={onPressRetry}
												isLastMessage={index === messages.length - 1}
												isFirstMessage={index === 0}
											/>

											{shouldShowFollowUpHeader && (
												<CustomListSubheader
													enableGutters={false}
													style={{
														paddingVertical: 0,
														alignSelf: "flex-start",
													}}
												>
													Follow-ups
												</CustomListSubheader>
											)}
										</Box>
										{shouldShowRecommendedChips && (
											<Box>
												{showRecommendedChipsLoading ? (
													<Box
														flexDirection="row"
														alignItems="center"
														paddingLeft={16}
														gap={12}
													>
														<RecommendedChipsSkeleton
															numLines={1}
															alignSelf="flex-end"
															skeletonHeight={52}
															width="63.5%"
														/>
														<RecommendedChipsSkeleton
															numLines={1}
															alignSelf="flex-end"
															skeletonHeight={52}
															width="36.5%"
														/>
													</Box>
												) : (
													<Box paddingHorizontal={16}>
														<ChatStartViewGroup
															titleText={`Hey there, I'm **${assistantInfo.alias}**. How can I help?`}
															isStarterMessage={false}
															image={assistantInfo.icon}
															followUpMessages={[
																...recommendedQueries.map((query) => ({
																	message: query,
																	onPress: () => handleSelectQuery(query),
																	type: "starterMessage" as const,
																})),
																...(showSummarizeResponse
																	? [
																			{
																				message: "Summarize this page",
																				onPress: () =>
																					handleSelectQuery(
																						"Summarize this page",
																					),
																				type: "starterMessage" as const,
																			},
																		]
																	: []),
															]}
														/>
													</Box>
												)}
											</Box>
										)}
									</Box>
								</FadeView>
							);
						},
					)}
				</>
			) : (
				<Box justifyContent="center" alignItems="center" flex={1}>
					<ChatStartViewGroup
						titleText={messages[0].content}
						isStarterMessage={true}
						image={assistantInfo.icon}
						followUpMessages={recommendedQueries.map((query) => ({
							message: query,
							onPress: () => handleSelectQuery(query),
							type: "starterMessage" as const,
						}))}
					/>
				</Box>
			)}
		</>
	);
};
